
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import {
  Mail, Send, FileText, Clock, CheckCircle2, AlertCircle, Plus,
  Eye, Trash2, Receipt, Bell, ShieldCheck, Megaphone, Copy
} from "lucide-react";

const TEMPLATE_TYPES = [
  { value: "invoice", label: "Invoice Notification", icon: Receipt, description: "Send invoice details to clients" },
  { value: "payment_reminder", label: "Payment Reminder", icon: Bell, description: "Remind clients about pending payments" },
  { value: "gst_alert", label: "GST Alert", icon: ShieldCheck, description: "Notify about GST deadlines and updates" },
  { value: "product_update", label: "Product Update", icon: Megaphone, description: "Share product news and updates" },
  { value: "custom", label: "Custom Email", icon: Mail, description: "Write a custom email" },
];

const TEMPLATE_CONTENT: Record<string, { subject: string; body: string }> = {
  invoice: {
    subject: "Invoice #{invoiceNumber} from {companyName}",
    body: `Dear {recipientName},

Please find below the details of your invoice:

Invoice Number: #{invoiceNumber}
Amount: ₹{amount}
Due Date: {dueDate}

Payment Details:
- UPI: {upiId}
- Bank: {bankDetails}

Please make the payment by the due date to avoid any late fees.

If you have already made the payment, please ignore this email.

Thank you for your business!

Best regards,
{companyName}`,
  },
  payment_reminder: {
    subject: "Payment Reminder: Invoice #{invoiceNumber} - ₹{amount} Due",
    body: `Dear {recipientName},

This is a friendly reminder that payment for Invoice #{invoiceNumber} amounting to ₹{amount} is due on {dueDate}.

Outstanding Amount: ₹{amount}
Days Overdue: {daysOverdue}

Please arrange the payment at your earliest convenience.

Payment Options:
- UPI: {upiId}
- Bank Transfer: {bankDetails}
- Online: {paymentLink}

If you have already made the payment, please share the transaction details so we can update our records.

Thank you,
{companyName}`,
  },
  gst_alert: {
    subject: "GST {returnType} Filing Deadline - {deadline}",
    body: `Dear {recipientName},

This is a reminder about your upcoming GST compliance deadline:

Return Type: {returnType}
Filing Period: {period}
Due Date: {deadline}
Status: {status}

Action Required:
{actionItems}

Please ensure timely filing to avoid penalties. Late filing attracts a penalty of ₹50/day (₹20/day for NIL returns).

Need help? Contact your assigned CA or reach out through the AccouPro marketplace.

Regards,
AccouPro Compliance Team`,
  },
  product_update: {
    subject: "{updateTitle} - AccouPro Update",
    body: `Dear {recipientName},

We're excited to share the latest updates from AccouPro:

{updateTitle}

{updateContent}

Key Highlights:
{highlights}

To explore these features, log in to your AccouPro dashboard.

If you have any questions, our support team is ready to help.

Best regards,
The AccouPro Team`,
  },
  custom: {
    subject: "",
    body: "",
  },
};

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof CheckCircle2 }> = {
  draft: { label: "Draft", color: "bg-muted text-muted-foreground", icon: FileText },
  queued: { label: "Queued", color: "bg-primary/10 text-primary", icon: Clock },
  sent: { label: "Sent", color: "bg-emerald-500/10 text-emerald-600", icon: CheckCircle2 },
  failed: { label: "Failed", color: "bg-destructive/10 text-destructive", icon: AlertCircle },
};

export default function EmailNotificationsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showCompose, setShowCompose] = useState(false);
  const [showPreview, setShowPreview] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState("custom");
  const [form, setForm] = useState({ recipientEmail: "", recipientName: "", subject: "", body: "" });

  const { data: notifications = [] } = useQuery({
    queryKey: ["email-notifications"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_notifications")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (status: string) => {
      if (!user) throw new Error("Not logged in");

      // If sending, call edge function first
      if (status === "queued") {
        const { data: sendData, error: sendError } = await supabase.functions.invoke("send-email", {
          body: {
            to: form.recipientEmail.trim(),
            toName: form.recipientName.trim() || undefined,
            subject: form.subject.trim(),
            body: form.body.trim(),
          },
        });
        if (sendError) throw sendError;
        if (sendData && !sendData.success) throw new Error(sendData.error || "Failed to send email");
        status = "sent";
      }

      const { error } = await supabase.from("email_notifications").insert({
        sender_user_id: user.id,
        recipient_email: form.recipientEmail.trim(),
        recipient_name: form.recipientName.trim() || null,
        subject: form.subject.trim(),
        body: form.body.trim(),
        template_type: selectedTemplate,
        status,
        sent_at: status === "sent" ? new Date().toISOString() : null,
      });
      if (error) throw error;
    },
    onSuccess: (_, status) => {
      toast({ title: status === "draft" ? "Saved as draft" : "Email sent successfully! ✉️" });
      setShowCompose(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ["email-notifications"] });
    },
    onError: (err: any) => toast({ title: "Error", description: err?.message || "Failed to process email", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("email_notifications").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Draft deleted" });
      queryClient.invalidateQueries({ queryKey: ["email-notifications"] });
    },
  });

  const resetForm = () => {
    setForm({ recipientEmail: "", recipientName: "", subject: "", body: "" });
    setSelectedTemplate("custom");
  };

  const applyTemplate = (type: string) => {
    setSelectedTemplate(type);
    const tpl = TEMPLATE_CONTENT[type];
    if (tpl) setForm((f) => ({ ...f, subject: tpl.subject, body: tpl.body }));
  };

  const stats = {
    total: notifications.length,
    sent: notifications.filter((n: any) => n.status === "sent").length,
    queued: notifications.filter((n: any) => n.status === "queued").length,
    drafts: notifications.filter((n: any) => n.status === "draft").length,
  };

  const previewItem = notifications.find((n: any) => n.id === showPreview);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Email Notifications</h1>
          <p className="text-muted-foreground text-sm">Manage invoices, reminders, GST alerts & more</p>
        </div>
        <Dialog open={showCompose} onOpenChange={(open) => { setShowCompose(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-2" />Compose Email</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Compose Email Notification</DialogTitle></DialogHeader>
            <div className="space-y-4">
              {/* Template picker */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Choose Template</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {TEMPLATE_TYPES.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => applyTemplate(t.value)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border text-center transition-colors ${
                        selectedTemplate === t.value
                          ? "border-primary bg-primary/5 text-primary"
                          : "border-border hover:border-primary/50 text-muted-foreground"
                      }`}
                    >
                      <t.icon className="h-5 w-5" />
                      <span className="text-xs font-medium">{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Recipient Email</label>
                  <Input placeholder="client@example.com" value={form.recipientEmail} onChange={(e) => setForm({ ...form, recipientEmail: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Recipient Name</label>
                  <Input placeholder="Client Name" value={form.recipientName} onChange={(e) => setForm({ ...form, recipientName: e.target.value })} />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Subject</label>
                <Input placeholder="Email subject..." value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Body</label>
                <Textarea placeholder="Email content..." rows={12} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="font-mono text-xs" />
                <p className="text-[10px] text-muted-foreground mt-1">Use {"{variable}"} placeholders — replace before sending</p>
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => saveMutation.mutate("draft")} disabled={!form.subject.trim() || !form.recipientEmail.trim()}>
                  <FileText className="h-4 w-4 mr-2" />Save Draft
                </Button>
                <Button onClick={() => saveMutation.mutate("queued")} disabled={!form.subject.trim() || !form.recipientEmail.trim() || !form.body.trim() || saveMutation.isPending}>
                  <Send className="h-4 w-4 mr-2" />{saveMutation.isPending ? "Sending..." : "Send Now"}
                </Button>
              </div>

              <div className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                <p className="text-xs text-emerald-600 flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Resend is connected. Emails will be delivered via AccouPro branded templates.
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Emails", value: stats.total, icon: Mail },
          { label: "Sent", value: stats.sent, icon: CheckCircle2 },
          { label: "Queued", value: stats.queued, icon: Clock },
          { label: "Drafts", value: stats.drafts, icon: FileText },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold text-foreground">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-3">
          {notifications.length === 0 && (
            <div className="text-center py-16">
              <Mail className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">No email notifications yet. Compose your first one!</p>
            </div>
          )}
          {notifications.map((n: any) => {
            const sc = STATUS_CONFIG[n.status] || STATUS_CONFIG.draft;
            const StatusIcon = sc.icon;
            return (
              <Card key={n.id} className="hover:bg-muted/20 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${sc.color}`}>
                      <StatusIcon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="font-medium text-foreground text-sm truncate">{n.subject}</h3>
                        <Badge variant="outline" className="text-[10px] h-5 capitalize">{n.template_type.replace("_", " ")}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        To: {n.recipient_email}{n.recipient_name ? ` (${n.recipient_name})` : ""} • {new Date(n.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowPreview(n.id)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {n.status === "draft" && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteMutation.mutate(n.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="templates" className="space-y-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {TEMPLATE_TYPES.filter((t) => t.value !== "custom").map((t) => {
              const tpl = TEMPLATE_CONTENT[t.value];
              return (
                <Card key={t.value} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10 text-primary">
                        <t.icon className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-sm">{t.label}</CardTitle>
                        <CardDescription className="text-xs">{t.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground mb-3 line-clamp-3 font-mono">{tpl.body.slice(0, 120)}...</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        applyTemplate(t.value);
                        setShowCompose(true);
                      }}
                    >
                      <Copy className="h-3.5 w-3.5 mr-2" />Use Template
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Preview dialog */}
      <Dialog open={!!showPreview} onOpenChange={() => setShowPreview(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Email Preview</DialogTitle></DialogHeader>
          {previewItem && (
            <div className="space-y-4">
              <div className="space-y-2 text-sm">
                <div className="flex gap-2"><span className="text-muted-foreground w-16">To:</span><span className="text-foreground">{previewItem.recipient_email}</span></div>
                <div className="flex gap-2"><span className="text-muted-foreground w-16">Subject:</span><span className="font-medium text-foreground">{previewItem.subject}</span></div>
                <div className="flex gap-2"><span className="text-muted-foreground w-16">Type:</span><Badge variant="outline" className="capitalize text-xs">{previewItem.template_type.replace("_", " ")}</Badge></div>
                <div className="flex gap-2"><span className="text-muted-foreground w-16">Status:</span><Badge className={STATUS_CONFIG[previewItem.status]?.color}>{STATUS_CONFIG[previewItem.status]?.label}</Badge></div>
              </div>
              <div className="border rounded-lg p-4 bg-muted/20">
                <pre className="whitespace-pre-wrap text-xs text-foreground font-sans">{previewItem.body}</pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
